import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import dayjs from "dayjs";

import { selectCurrentAllocation, useGetAccountSubscriptionsQuery, useGetSubscriptionTiersQuery } from "fond/api";
import {
  EXCEED_SUBSCRIPTION_QUOTA_KEY,
  EXPIRED_BANNER_KEY,
  NO_LICENSE_ADMIN,
  NO_LICENSE_MEMBER,
  SOON_TO_EXPIRED_BANNER_KEY,
  TRIAL_BANNER_KEY,
  TRIAL_TIER,
} from "fond/constants";
import { BillingCycle, Subscription, SubscriptionStatus } from "fond/types";

import { useGetBillingCycles } from "./useGetBillingCycles";
import { useGetCurrentActiveSubscription, useGetCurrentSubscription } from "./useGetCurrentSubscription";
import { useRoleCheck } from "./useRoleCheck";

const SOON_TO_EXPIRE_IN_DAYS = 14;
const SOON_TO_EXPIRE_TRIAL_IN_DAYS = 5;

interface UseCanIPlannerReturn {
  canBuy: boolean;
  canUpgrade: boolean;
  hasSub: boolean;
}

export type BannerType = "soon to expire" | "expired" | "quota exceeded" | "admin without license" | "member without license" | "trial";
interface useUserManagementBannerReturn {
  currentBillingCycle?: BillingCycle;
  showBanner: boolean;
  subscription?: Subscription;
  bannerType?: BannerType;
}

/**
 * Determine whether the user can buy or upgrade to the FOND Planner add-on
 *
 * Buying the add-on is only allowed when:
 * 1. The current user is an admin of the account
 * 2. There is no existing subscription with the FOND Planner add-on
 *
 * Upgrading the add-on is only allowed when:
 * 1. The current user is an admin of the account
 * 2. There is no pending billing cycle (i.e. a pending upgrade or downgrade)
 * 3. There exists a higher tier than the current tier
 */
export const useCanIPlanner = (): UseCanIPlannerReturn => {
  const { active: billingCycle, pending: pendingBillingCycle } = useGetBillingCycles();
  const isAdmin = useRoleCheck("admin");

  const { data: tiers, isLoading: isTiersLoading } = useGetSubscriptionTiersQuery();

  const canBuy = isAdmin && !billingCycle?.Planner && !pendingBillingCycle;

  // Determine if the user can upgrade from their current tier
  const currentTier = tiers?.find((tier) => tier.ID === billingCycle?.Planner?.TierID);
  const canUpgradeFromCurrentTier = !isTiersLoading && tiers ? tiers.some((tier) => tier.TierLevel > (currentTier?.TierLevel || 0)) : false;
  const canUpgrade = !pendingBillingCycle && isAdmin && canUpgradeFromCurrentTier;
  const hasSub = !!(billingCycle || pendingBillingCycle);
  return { canBuy, canUpgrade, hasSub };
};

/**
 * Determine whether the user management banner is to be displayed
 */
export const useUserManagementBanner = (): useUserManagementBannerReturn => {
  const currentAllocation = useSelector(selectCurrentAllocation);
  const accountId = currentAllocation?.Account.ID;
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(accountId ?? skipToken);
  const { active: currentBillingCycle, expired: expiredBillingCycle } = useGetBillingCycles();
  const activeSubscription = useGetCurrentActiveSubscription();
  const currentSubscription = useGetCurrentSubscription();
  const isTrial = currentBillingCycle?.Planner?.Tier === TRIAL_TIER;

  // Do not show the banner if no subscription at all.
  if (!subscriptions || subscriptions.length === 0) return { showBanner: false };

  // Expire
  if (currentSubscription?.Status === SubscriptionStatus.Expired && expiredBillingCycle) {
    const showExpiredBanner = !sessionStorage.getItem(EXPIRED_BANNER_KEY);
    return { showBanner: showExpiredBanner, subscription: currentSubscription, bannerType: "expired", currentBillingCycle: expiredBillingCycle };
  }

  // Quota used up
  const NEARLY_EXHAUSTED_QUOTA_THRESHOLD = 0.9; // 90%
  if (
    activeSubscription?.Status === SubscriptionStatus.Active &&
    activeSubscription.Planner &&
    activeSubscription.Planner.QuotaUsage + (activeSubscription.DataPackUsage ?? 0) >=
      (activeSubscription.Planner.Quota + (activeSubscription.DataPackQuota ?? 0)) * NEARLY_EXHAUSTED_QUOTA_THRESHOLD
  ) {
    const showExceedQuotaBanner = !sessionStorage.getItem(EXCEED_SUBSCRIPTION_QUOTA_KEY);
    return { showBanner: showExceedQuotaBanner, subscription: activeSubscription, bannerType: "quota exceeded" };
  }

  // Soon to expire
  // - Standard subscription = SOON_TO_EXPIRE_IN_DAYS (14 days)
  // - Trial subscription = SOON_TO_EXPIRE_TRIAL_IN_DAYS (5 days)
  if (
    activeSubscription &&
    currentBillingCycle &&
    dayjs(activeSubscription.EndTime).utc(true).diff(dayjs(), "days") <= (isTrial ? SOON_TO_EXPIRE_TRIAL_IN_DAYS : SOON_TO_EXPIRE_IN_DAYS) &&
    !sessionStorage.getItem(SOON_TO_EXPIRED_BANNER_KEY)
  ) {
    return { showBanner: true, subscription: currentSubscription, bannerType: "soon to expire", currentBillingCycle };
  }

  /**
   * User is an admin to an account with subscription, but does not have license
   */
  if (
    currentSubscription?.Status === SubscriptionStatus.Active &&
    currentBillingCycle &&
    currentAllocation?.Role === "admin" &&
    !currentAllocation.License &&
    !sessionStorage.getItem(NO_LICENSE_ADMIN)
  ) {
    return {
      showBanner: true,
      subscription: currentSubscription,
      bannerType: "admin without license",
      currentBillingCycle,
    };
  }

  /**
   * User is a member to an account with subscription, but does not have license
   */
  if (
    currentSubscription?.Status === SubscriptionStatus.Active &&
    currentBillingCycle &&
    currentAllocation?.Role === "member" &&
    !currentAllocation.License &&
    !sessionStorage.getItem(NO_LICENSE_MEMBER)
  ) {
    return {
      showBanner: true,
      subscription: currentSubscription,
      bannerType: "member without license",
      currentBillingCycle,
    };
  }

  if (currentBillingCycle?.Planner?.Tier === TRIAL_TIER && !sessionStorage.getItem(TRIAL_BANNER_KEY)) {
    return {
      showBanner: true,
      subscription: currentSubscription,
      bannerType: "trial",
      currentBillingCycle,
    };
  }

  return { showBanner: false };
};
